/**
 * Utility functions for handling Widen DAM image URLs
 */

/**
 * Detects if a URL is from Widen DAM
 * @param url - The image URL to check
 * @returns true if the URL is from Widen DAM
 */
export const isWidenUrl = (url: string): boolean => {
  if (!url) return false;
  return url.includes("widen.net") || url.includes("widencollective.com");
};

/**
 * Converts a Widen image URL to WebP format
 * Handles both path-based format specifications (e.g., /jpeg/, /png/) and file extensions
 * @param url - The original Widen image URL
 * @returns The URL converted to WebP format, or original URL if conversion fails
 *
 * @example
 * // Path-based format
 * convertWidenUrlToWebP('https://collette.widen.net/content/abc123/jpeg/image.jpeg')
 * // Returns: 'https://collette.widen.net/content/abc123/webp/image.webp'
 *
 * @example
 * // With query parameters
 * convertWidenUrlToWebP('https://collette.widen.net/content/abc123/jpeg/image.jpeg?w=620&h=410')
 * // Returns: 'https://collette.widen.net/content/abc123/webp/image.webp?w=620&h=410'
 */
export const convertWidenUrlToWebP = (url: string): string => {
  if (!url || !isWidenUrl(url)) {
    return url;
  }

  // Early return if already WebP format
  if (url.includes("/webp/") || /\.webp$/i.test(url.split("?")[0])) {
    return url;
  }

  try {
    // Split URL into base and query string
    const [baseUrl, queryString] = url.split("?");

    // Convert path-based format: /jpeg/ -> /webp/, /png/ -> /webp/, etc.
    let convertedUrl = baseUrl
      .replace(/\/jpeg\//gi, "/webp/")
      .replace(/\/jpg\//gi, "/webp/")
      .replace(/\/png\//gi, "/webp/")
      .replace(/\/gif\//gi, "/webp/");

    // Convert file extension: .jpeg -> .webp, .jpg -> .webp, etc.
    convertedUrl = convertedUrl
      .replace(/\.jpeg$/gi, ".webp")
      .replace(/\.jpg$/gi, ".webp")
      .replace(/\.png$/gi, ".webp")
      .replace(/\.gif$/gi, ".webp");

    // Reattach query string if it exists
    return queryString ? `${convertedUrl}?${queryString}` : convertedUrl;
  } catch (error) {
    // If anything goes wrong, return the original URL
    console.warn("Failed to convert Widen URL to WebP:", error);
    return url;
  }
};

/**
 * Processes an image URL, converting to WebP if it's from Widen DAM
 * @param url - The image URL to process
 * @returns The processed URL (WebP if Widen, otherwise unchanged)
 */
export const processImageUrl = (url: string): string => {
  if (isWidenUrl(url)) {
    return convertWidenUrlToWebP(url);
  }
  return url;
};
